import React from 'react';
import I18n from 'i18n-js';

import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { isItemPage } from '@reverbdotcom/commons/src//url_helpers';
import { useURL } from '@reverbdotcom/commons/src/url_context';

import SanitizedRender from './sanitized_render';
import AffirmLearnMoreModal from './affirm_learn_more_modal';
import { RCIcon } from '@reverbdotcom/cadence/components';
import AffirmLogo from '@reverbdotcom/cadence/images/vendor/affirm-logo.svg?component';

interface IProps {
  availableTermMonths: (number | null)[] | null;
  buyerPrice: string;
  estimatedMonthlyPayment: string;
  maxTermMonth: number;
  zeroPercentFinancing?: boolean;
  lowestAprAtMaxTerm?: number;
  splitPay?: boolean;
}

AffirmEstimateCTA.defaultProps = {
  zeroPercentFinancing: false,
  lowestAprAtMaxTerm: null,
};

export function AffirmExperimentCallout({
  availableTermMonths,
  buyerPrice,
  estimatedMonthlyPayment,
  maxTermMonth,
  zeroPercentFinancing,
  lowestAprAtMaxTerm,
  splitPay,
}: IProps) {
  return (
    <div className="affirm-estimate-cta__new">
      <div className="affirm-estimate-cta__logo">
        <RCIcon
          svgComponent={AffirmLogo}
          title={I18n.t('commons.affirmEstimateCTA.affirmLogo')}
        />
      </div>
      <div>
        <SanitizedRender
          className="affirm-estimate-cta__copy"
          html={
            I18n.t(zeroPercentFinancing ? 'commons.affirmEstimateCTA.interestFree' : 'commons.affirmEstimateCTA.asLowAs', {
              price: estimatedMonthlyPayment,
              tagOpen: '<strong>',
              tagClose: '</strong>',
            })
          }
        />
        <div>
          <AffirmLearnMoreModal
            estimatedMonthlyPayment={estimatedMonthlyPayment}
            buyerPrice={buyerPrice}
            maxTermMonth={maxTermMonth}
            availableTermMonths={availableTermMonths}
            zeroPercentFinancing={zeroPercentFinancing}
            lowestAprAtMaxTerm={lowestAprAtMaxTerm}
            splitPay={splitPay}
          />
        </div>
      </div>
    </div>
  );
}

function AffirmEstimateCTA({
  availableTermMonths,
  buyerPrice,
  estimatedMonthlyPayment,
  maxTermMonth,
  zeroPercentFinancing,
  lowestAprAtMaxTerm,
  splitPay,
}: IProps) {
  const user = useUser();
  const url = useURL();
  const isExpEnabled = useExpEnabled(experiments.AFFIRM_PRESENTMENT_ON_ITEM_PRICING);
  const showExperiment = isExpEnabled && isItemPage(url);

  useViewTracking({
    componentName: 'AffirmEstimateCTA',
    eventName: MParticleEventName.ComponentView,
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.AFFIRM_PRESENTMENT_ON_ITEM_PRICING,
    ]),
  }, !!estimatedMonthlyPayment && isItemPage(url));

  if (!estimatedMonthlyPayment) { return null; }

  return showExperiment ? (
    <AffirmExperimentCallout
      availableTermMonths={availableTermMonths}
      buyerPrice={buyerPrice}
      estimatedMonthlyPayment={estimatedMonthlyPayment}
      maxTermMonth={maxTermMonth}
      zeroPercentFinancing={zeroPercentFinancing}
      lowestAprAtMaxTerm={lowestAprAtMaxTerm}
      splitPay={splitPay}
    />
  ) : (
    <div className="affirm-estimate-cta">
      <SanitizedRender
        className="affirm-estimate-cta__copy"
        html={
          I18n.t(splitPay ? 'commons.affirmEstimateCTA.splitPay' : 'commons.affirmEstimateCTA.asLowAsWith', {
            price: estimatedMonthlyPayment,
            tagOpen: '<strong>',
            tagClose: '</strong>',
          })
        }
      />
      <span className="affirm-estimate-cta__logo">
        <RCIcon
          svgComponent={AffirmLogo}
          title={I18n.t('commons.affirmEstimateCTA.affirmLogo')}
          inline
        />
      </span>
      {zeroPercentFinancing &&
        <span className="affirm-estimate-cta__zero-percent">
          {I18n.t('commons.affirmEstimateCTA.zeroPercent')}
        </span>
      }
      <div className="affirm-estimate-cta__learn-more">
        <AffirmLearnMoreModal
          estimatedMonthlyPayment={estimatedMonthlyPayment}
          buyerPrice={buyerPrice}
          maxTermMonth={maxTermMonth}
          availableTermMonths={availableTermMonths}
          zeroPercentFinancing={zeroPercentFinancing}
          lowestAprAtMaxTerm={lowestAprAtMaxTerm}
          splitPay={splitPay}
        />
      </div>
    </div>
  );
}

export default AffirmEstimateCTA;
